import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { SignUp } from "@clerk/clerk-react";
import type { CheckedState } from "@radix-ui/react-checkbox";
import { ShieldCheck, FileText, AlertCircle, Shield } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { LegalLink } from "@/components/LegalModal";
import { RecaptchaV3 } from "@/components/RecaptchaV3";

import { RecaptchaProvider } from "@/components/RecaptchaProvider";
import { validateCoupon } from "@/lib/api";

const SignUpPageContent = () => {
  const [location] = useLocation();
  // Ensure we pass the base path to Clerk, not the full sub-path
  const signUpPath = location.startsWith("/sign-up") ? "/sign-up" : "/signup";
  const FEATURE_COUPONS = (import.meta as any).env?.VITE_FEATURE_COUPONS === 'true';

  // Initialize from localStorage to persist across re-renders
  const [agreements, setAgreements] = useState<{ terms: boolean; privacy: boolean }>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('hipo-signup-agreements');
      return saved ? JSON.parse(saved) : { terms: false, privacy: false };
    }
    return { terms: false, privacy: false };
  });

  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>("");

  // Coupon state (minimal)
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState<string | null>(null);
  const [couponStatus, setCouponStatus] = useState<"idle" | "checking" | "applied" | "invalid" | "error">("idle");

  const handleApplyCoupon = useCallback(async () => {
    const code = (coupon || "").trim();
    if (!code) return;
    setCouponStatus("checking");
    setCouponMsg(null);
    try {
      const resp = await validateCoupon(code);
      if (resp.valid) {
        // Store for post-auth redemption in SSO callback
        if (typeof window !== 'undefined') {
          localStorage.setItem('hipo_coupon_pending', JSON.stringify({ code, ts: Date.now(), label: resp.label || null }));
        }
        setCouponStatus("applied");
        setCouponMsg(resp.label ? `Applied: ${resp.label}` : "Code applied");
      } else {
        setCouponStatus("invalid");
        setCouponMsg(resp.reason ? `Invalid: ${resp.reason}` : "Invalid code");
      }
    } catch (e) {
      setCouponStatus("error");
      setCouponMsg("Could not validate code. Try again.");
    }
  }, [coupon]);

  // Persist agreements to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('hipo-signup-agreements', JSON.stringify(agreements));
    }
  }, [agreements]);

  const handleAgreementChange = (key: "terms" | "privacy") => (checked: CheckedState) => {
    // Once checked, lock the checkbox to prevent confusion
    if (checked === true) {
      setAgreements((prev) => ({ ...prev, [key]: true }));
    }
  };

  const handleCaptchaValidation = useCallback((isValid: boolean, token?: string) => {
    setCaptchaValid(isValid);
    setCaptchaToken(token || "");
  }, []);

  const allAccepted = agreements.terms && agreements.privacy && captchaValid;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 font-sans">
      <div className="grid w-full max-w-5xl gap-8 md:grid-cols-2">
        <section className="space-y-6 rounded-xl border border-border bg-card p-8 shadow-sm">
          <div className="space-y-2">
            <h1 className="font-display text-3xl font-semibold text-foreground">Create your HiPo account</h1>
            <p className="text-sm text-muted-foreground">
              Transparency first. Please review and accept our legal agreements before continuing with sign-up.
            </p>
          </div>

          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-terms"
                checked={agreements.terms}
                onCheckedChange={handleAgreementChange("terms")}
                aria-describedby="terms-help"
                disabled={agreements.terms}
              />
              <Label htmlFor="accept-terms" className={`leading-relaxed ${agreements.terms ? 'text-green-700 font-medium cursor-default' : 'cursor-pointer'}`}>
                I have read and agree to the {" "}
                <LegalLink type="terms">
                  Terms of Use
                </LegalLink>
                .
                {agreements.terms && <span className="ml-2 text-green-600">✓ Confirmed</span>}
              </Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-privacy"
                checked={agreements.privacy}
                onCheckedChange={handleAgreementChange("privacy")}
                aria-describedby="privacy-help"
                disabled={agreements.privacy}
              />
              <Label htmlFor="accept-privacy" className={`leading-relaxed ${agreements.privacy ? 'text-green-700 font-medium cursor-default' : 'cursor-pointer'}`}>
                I have reviewed and accept the {" "}
                <LegalLink type="privacy">
                  Privacy Policy
                </LegalLink>
                .
                {agreements.privacy && <span className="ml-2 text-green-600">✓ Confirmed</span>}
              </Label>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-xs text-primary">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p id="terms-help">
              Once both boxes are checked and email is verified, the sign-up form unlocks. You can revisit the policies anytime from the landing and settings pages.
            </p>
          </div>

          {/* reCAPTCHA v3 Security Check */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Security Verification</Label>
            <div className="p-3 rounded-lg border border-primary/20 bg-primary/5">
              <RecaptchaV3 onValidation={handleCaptchaValidation} action="signup" />
            </div>
          </div>

          {!captchaValid && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <Shield className="h-4 w-4 text-blue-600 dark:text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800 dark:text-blue-200">
                <p className="font-medium">Security Verification</p>
                <p className="mt-1">Please wait while we verify you are human. This helps protect against automated attacks.</p>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-6 shadow-lg">
          {/* Coupon input (flag-gated; validation stored for post-auth redemption) */}
          {FEATURE_COUPONS && (
          <div className="mb-6 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4">
            <label className="block text-sm font-medium mb-2">Have a code?</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Enter code"
                className="flex-1 rounded-md border border-border px-3 py-2 text-sm"
                aria-label="Coupon code"
              />
              <button
                type="button"
                onClick={handleApplyCoupon}
                className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90"
                disabled={!coupon.trim() || couponStatus === 'checking'}
                title="Apply code"
              >
                {couponStatus === 'checking' ? 'Checking…' : 'Apply'}
              </button>
            </div>
            {couponMsg && (
              <p className={`mt-2 text-xs ${couponStatus === 'applied' ? 'text-green-700' : (couponStatus === 'invalid' || couponStatus === 'error') ? 'text-red-600' : 'text-muted-foreground'}`}>
                {couponMsg}
              </p>
            )}
          </div>
          )}

          {allAccepted ? (
            <SignUp
              path={signUpPath}
              routing="path"
              signInUrl="/sign-in"
              redirectUrl="/sign-up/sso-callback"
              afterSignUpUrl="/onboarding/step-1"
              afterSignInUrl="/dashboard"
              appearance={{
                elements: {
                  formButtonPrimary:
                    'bg-primary text-primary-foreground hover:bg-primary/90',
                },
              }}
            />
          ) : (
            <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-4 text-center text-muted-foreground">
              <FileText className="h-12 w-12 text-primary" aria-hidden="true" />
              <p className="text-sm font-medium text-foreground">Complete the security requirements to continue:</p>
              <ul className="text-xs text-left space-y-2">
                <li className={agreements.terms ? "text-green-600" : ""}>✓ Accept Terms of Use</li>
                <li className={agreements.privacy ? "text-green-600" : ""}>✓ Accept Privacy Policy</li>
                <li className={captchaValid ? "text-green-600" : ""}>✓ Security verification</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-4">
                These agreements explain how we protect your data and ensure account security.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const SignUpPage = () => (
  <RecaptchaProvider>
    <SignUpPageContent />
  </RecaptchaProvider>
);

export default SignUpPage;
